import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import UrlInput from "./UrlInput";
import CustomTargetAdvanced from "./CustomTargetAdvanced";
import {
  CUSTOM_TARGET_PY_SCRIPTS,
  CUSTOM_TARGET_USER_ENTERED_JSONS,
  CUSTOM_TARGET_ADVANCED,
} from "./customTargetHelpers";
import "../../styles/ProfilesManager.css";

const getCurrentTheme = () => {
  return document.body.getAttribute("data-theme") || "dark";
};

/**
 * Drop-in custom target UI: URL + Scripts / JSONs / Advanced.
 * Same component for Target Wizard (AI App / MCP / LLM) and Quick Start.
 */
const CustomTargetConfig = forwardRef(function CustomTargetConfig(
  {
    url = "",
    onUrlChange,
    urlPlaceholder,
    urlMaxLength = 500,
    mode = CUSTOM_TARGET_PY_SCRIPTS,
    onModeChange,
    inputScriptFile = null,
    outputScriptFile = null,
    onInputScriptChange,
    onOutputScriptChange,
    inputScriptDescription,
    outputScriptDescription,
    promptsProcessing = "single",
    onPromptsProcessingChange,
    requestParams = "",
    responseParams = "",
    onRequestParamsChange,
    onResponseParamsChange,
    onAdvancedOptionsChange,
    advancedSeed = null,
    existingInputScriptName = "",
    existingOutputScriptName = "",
    onDownloadExistingScript,
    className,
  },
  ref
) {
  const { t } = useTranslation("targets");
  const resolvedUrlPlaceholder = urlPlaceholder ?? t("form.urlPlaceholder");
  const resolvedInputScriptDescription =
    inputScriptDescription ?? t("form.inputScriptDesc");
  const resolvedOutputScriptDescription =
    outputScriptDescription ?? t("form.outputScriptDesc");
  const inputFileRef = useRef(null);
  const outputFileRef = useRef(null);
  const advancedRef = useRef(null);
  const [activeTab, setActiveTab] = useState(mode);

  useEffect(() => {
    setActiveTab(mode);
  }, [mode]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (inputFileRef.current) inputFileRef.current.value = "";
      if (outputFileRef.current) outputFileRef.current.value = "";
      advancedRef.current?.reset();
    },
  }));

  const handleDeleteInputScript = () => {
    onInputScriptChange(null);
    if (inputFileRef.current) inputFileRef.current.value = "";
  };

  const handleDeleteOutputScript = () => {
    onOutputScriptChange(null);
    if (outputFileRef.current) outputFileRef.current.value = "";
  };

  const inputScriptLabel = inputScriptFile?.name || existingInputScriptName || "";
  const outputScriptLabel = outputScriptFile?.name || existingOutputScriptName || "";

  return (
    <div className={className}>
      <UrlInput
        value={url}
        onChange={onUrlChange}
        placeholder={resolvedUrlPlaceholder}
        maxLength={urlMaxLength}
      />

      {/* Mode: Scripts / JSONs / Advanced */}
      <label className="pm-input-label" style={{ marginTop: "20px" }}>
        <span>{t("scriptsParameters.configuration", "Configuration")}</span>
        <div style={{ position: "relative", marginTop: "8px" }}>
          <select
            className="pm-input"
            value={activeTab}
            onChange={(e) => {
              const next = e.target.value;
              setActiveTab(next);
              onModeChange(next);
            }}
            style={{
              padding: "8px 36px 8px 12px",
              borderRadius: "6px",
              border: `1px solid ${getCurrentTheme() === "light" ? "#d1d5db" : "#374151"}`,
              backgroundColor: "#ffffff",
              color: "#000000",
              width: "100%",
              fontSize: "14px",
              appearance: "none",
              WebkitAppearance: "none",
              MozAppearance: "none",
            }}
          >
            <option value={CUSTOM_TARGET_PY_SCRIPTS}>
              {t("scriptsParameters.tabs.scripts")}
            </option>
            <option value={CUSTOM_TARGET_USER_ENTERED_JSONS}>
              {t("scriptsParameters.tabs.jsons")}
            </option>
            <option value={CUSTOM_TARGET_ADVANCED}>
              {t("scriptsParameters.tabs.advanced")}
            </option>
          </select>
          <span
            style={{
              position: "absolute",
              right: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
              <path fill="#6b7280" d="M6 9L1 4h10z" />
            </svg>
          </span>
        </div>
      </label>

      <div
        style={{
          marginTop: "16px",
          minHeight: "120px",
          borderRadius: "8px",
          padding: "4px",
          fontSize: "14px",
        }}
      >
        {activeTab === CUSTOM_TARGET_PY_SCRIPTS && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <div
              style={{
                background: "rgba(15, 23, 42, 0.9)",
                borderRadius: "10px",
                border: "1px solid rgba(148, 163, 184, 0.4)",
                padding: "12px 14px",
                display: "flex",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
              }}
            >
              {[
                {
                  value: "single",
                  label: t("scriptsParameters.single.label"),
                  tooltip: t("scriptsParameters.single.tooltip"),
                },
                {
                  value: "multi",
                  label: t("scriptsParameters.multi.label"),
                  tooltip: t("scriptsParameters.multi.tooltip"),
                },
              ].map((option) => (
                <label
                  key={option.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    color: "#e5e7eb",
                  }}
                >
                  <input
                    type="radio"
                    name="input-script-prompt-processing"
                    value={option.value}
                    checked={promptsProcessing === option.value}
                    onChange={(e) => onPromptsProcessingChange(e.target.value)}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                      accentColor: "#2563eb",
                    }}
                  />
                  {option.label}
                  <span
                    className="pm-tooltip-container"
                    onClick={(e) => e.preventDefault()}
                    style={{ marginLeft: "4px" }}
                  >
                    <span className="pm-tooltip-icon">i</span>
                    <span
                      className="pm-tooltip-text"
                      style={{
                        top: "auto",
                        bottom: "140%",
                        left: "50%",
                        right: "auto",
                        transform: "translateX(-50%)",
                        maxWidth: "280px",
                      }}
                    >
                      {option.tooltip}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            <div
              style={{
                background: "rgba(15, 23, 42, 0.9)",
                borderRadius: "10px",
                border: "1px solid rgba(148, 163, 184, 0.4)",
                padding: "12px 14px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontWeight: 600,
                    color: "#e5e7eb",
                    marginBottom: "2px",
                  }}
                >
                  {t("scriptsParameters.inputScript")}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#9ca3af",
                  }}
                >
                  {resolvedInputScriptDescription}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {inputScriptLabel && (
                  <>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#60a5fa",
                        fontWeight: 500,
                        maxWidth: "150px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={inputScriptLabel}
                    >
                      {inputScriptLabel}
                    </div>
                    {inputScriptFile ? (
                      <button
                        type="button"
                        onClick={handleDeleteInputScript}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#ef4444",
                          cursor: "pointer",
                          padding: "2px 4px",
                          fontSize: "14px",
                          display: "flex",
                          alignItems: "center",
                        }}
                        title={t("scriptsParameters.removeFile")}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    ) : onDownloadExistingScript ? (
                      <button
                        type="button"
                        onClick={() => onDownloadExistingScript(existingInputScriptName)}
                        title={t("view.downloadFile")}
                        aria-label={t("view.downloadFile")}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#60a5fa",
                          cursor: "pointer",
                          padding: "2px 4px",
                          fontSize: "13px",
                        }}
                      >
                        <i className="fas fa-download"></i>
                      </button>
                    ) : null}
                  </>
                )}
                <input
                  ref={inputFileRef}
                  type="file"
                  accept=".py"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      onInputScriptChange(file);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => inputFileRef.current?.click()}
                  style={{
                    background: "rgba(37, 99, 235, 0.15)",
                    border: "1px solid rgba(96, 165, 250, 0.45)",
                    borderRadius: "8px",
                    color: "#93c5fd",
                    cursor: "pointer",
                    padding: "6px 12px",
                    fontSize: "13px",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <i className="fas fa-file-upload" aria-hidden="true" />
                  {inputScriptLabel
                    ? t("scriptsParameters.replaceFile")
                    : t("scriptsParameters.chooseFile")}
                </button>
              </div>
            </div>

            <div
              style={{
                background: "rgba(15, 23, 42, 0.9)",
                borderRadius: "10px",
                border: "1px solid rgba(148, 163, 184, 0.4)",
                padding: "12px 14px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontWeight: 600,
                    color: "#e5e7eb",
                    marginBottom: "2px",
                  }}
                >
                  {t("scriptsParameters.outputScript")}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#9ca3af",
                  }}
                >
                  {resolvedOutputScriptDescription}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {outputScriptLabel && (
                  <>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#60a5fa",
                        fontWeight: 500,
                        maxWidth: "150px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={outputScriptLabel}
                    >
                      {outputScriptLabel}
                    </div>
                    {outputScriptFile ? (
                      <button
                        type="button"
                        onClick={handleDeleteOutputScript}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#ef4444",
                          cursor: "pointer",
                          padding: "2px 4px",
                          fontSize: "14px",
                          display: "flex",
                          alignItems: "center",
                        }}
                        title={t("scriptsParameters.removeFile")}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    ) : onDownloadExistingScript ? (
                      <button
                        type="button"
                        onClick={() => onDownloadExistingScript(existingOutputScriptName)}
                        title={t("view.downloadFile")}
                        aria-label={t("view.downloadFile")}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#60a5fa",
                          cursor: "pointer",
                          padding: "2px 4px",
                          fontSize: "13px",
                        }}
                      >
                        <i className="fas fa-download"></i>
                      </button>
                    ) : null}
                  </>
                )}
                <input
                  ref={outputFileRef}
                  type="file"
                  accept=".py"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      onOutputScriptChange(file);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => outputFileRef.current?.click()}
                  style={{
                    background: "rgba(37, 99, 235, 0.15)",
                    border: "1px solid rgba(96, 165, 250, 0.45)",
                    borderRadius: "8px",
                    color: "#93c5fd",
                    cursor: "pointer",
                    padding: "6px 12px",
                    fontSize: "13px",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <i className="fas fa-file-upload" aria-hidden="true" />
                  {outputScriptLabel
                    ? t("scriptsParameters.replaceFile")
                    : t("scriptsParameters.chooseFile")}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === CUSTOM_TARGET_USER_ENTERED_JSONS && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "16px",
                width: "100%",
              }}
            >
              <div style={{ flex: 1 }}>
                <label className="pm-input-label">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <span>{t("scriptsParameters.request")}</span>
                    <div
                      style={{
                        fontSize: "12px",
                        color:
                          (requestParams?.length || 0) > 50000
                            ? "#ef4444"
                            : "#9ca3af",
                      }}
                    >
                      {(requestParams?.length || 0)} / 50000
                    </div>
                  </div>
                  <textarea
                    className="pm-input"
                    rows="8"
                    value={requestParams || ""}
                    onChange={onRequestParamsChange}
                    maxLength={50000}
                    style={{
                      resize: "none",
                      overflowY: "auto",
                    }}
                  />
                </label>
              </div>
              <div style={{ flex: 1 }}>
                <label className="pm-input-label">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <span>{t("scriptsParameters.response")}</span>
                    <div
                      style={{
                        fontSize: "12px",
                        color:
                          (responseParams?.length || 0) > 50000
                            ? "#ef4444"
                            : "#9ca3af",
                      }}
                    >
                      {(responseParams?.length || 0)} / 50000
                    </div>
                  </div>
                  <textarea
                    className="pm-input"
                    rows="8"
                    value={responseParams || ""}
                    onChange={onResponseParamsChange}
                    maxLength={50000}
                    style={{
                      resize: "none",
                      overflowY: "auto",
                    }}
                  />
                </label>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: activeTab === CUSTOM_TARGET_ADVANCED ? "block" : "none" }}>
          <CustomTargetAdvanced
            ref={advancedRef}
            url={url}
            onChange={onAdvancedOptionsChange}
            advancedSeed={advancedSeed}
          />
        </div>
      </div>
    </div>
  );
});

export default CustomTargetConfig;
